import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useReferProgram } from "@/hooks/followup/useReferProgram";
import { useDebounce } from "@/hooks/use-debounce";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useAutoSaveInit } from "@/hooks/useAutoSaveInit";

const MAX_INSTRUCTIONS = 45;
const PROGRAM_NONE = "__none__";

interface ReferProgramCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  parentType?: "prescription" | "meal_plan";
}

export const ReferProgramCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescription",
}: ReferProgramCardProps) => {
  const {
    data: programData,
    create,
    update,
    isCreating,
    isUpdating,
  } = useReferProgram(appointmentId, parentType);
  const { isInitialized, markInitialized } = useAutoSaveInit([
    appointmentId,
    parentType,
  ]);

  const [program, setProgram] = useState<
    "diabetes_management" | "weight_management" | ""
  >("");
  const [instructions, setInstructions] = useState<string>("");

  useEffect(() => {
    if (programData && !isInitialized.current) {
      setProgram(programData.program);
      setInstructions(programData.program_instructions || "");
      markInitialized();
    }
  }, [programData, isInitialized, markInitialized]);

  const debouncedInstructions = useDebounce(instructions, 1000);

  useEffect(() => {
    if (!programData && !program) return;
    if (programData && !isInitialized.current) return;

    if (
      programData &&
      programData.program === program &&
      programData.program_instructions === debouncedInstructions
    ) {
      return;
    }

    if (!programData && !program) return;

    if (
      program &&
      program !== "diabetes_management" &&
      program !== "weight_management"
    )
      return;

    const validProgram = program as "diabetes_management" | "weight_management";

    const payload = {
      program: validProgram,
      program_instructions: debouncedInstructions,
    };

    if (programData) {
      update({ id: programData.id, data: payload });
    } else {
      create({ parent_type: parentType, ...payload });
    }
  }, [program, debouncedInstructions, programData, create, update, parentType]);

  const isSaving = isCreating || isUpdating;
  const isReasonRequired = !!program;
  const isReasonInvalid = isReasonRequired && instructions.trim() === "";
  const remaining = MAX_INSTRUCTIONS - instructions.length;

  return (
    <Widget>
      <WidgetHeader
        title="Zarity Program"
        hasInfo
        extras={
          isSaving ? (
            <Loader2 className="size-3 animate-spin text-muted-foreground" />
          ) : undefined
        }
      />
      <WidgetContent className="pt-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
          <div className="space-y-1.5 lg:col-span-4">
            <label className="z-label text-muted-foreground">Program</label>
            <Select
              value={program}
              onValueChange={(val) =>
                setProgram(
                  val === PROGRAM_NONE
                    ? ""
                    : (val as "diabetes_management" | "weight_management")
                )
              }
              disabled={isCompleted}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PROGRAM_NONE}>
                  <span className="text-muted-foreground">None</span>
                </SelectItem>
                <SelectItem value="diabetes_management">
                  Diabetes Management
                </SelectItem>
                <SelectItem value="weight_management">
                  Weight Management
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 lg:col-span-8">
            <label className="z-label text-muted-foreground">
              Reason for recommending
              {isReasonRequired && (
                <span className="text-destructive ml-0.5">*</span>
              )}
            </label>
            <Input
              type="text"
              placeholder="e.g. to stabilise sugar levels"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              disabled={isCompleted || !program}
              maxLength={MAX_INSTRUCTIONS}
              aria-invalid={isReasonInvalid}
            />
            <p
              className={cn(
                "z-hint text-right",
                remaining <= 10 ? "text-destructive" : "text-muted-foreground"
              )}
            >
              {remaining} left
            </p>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};
